import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import AssetIdLookupForm from "./AssetIdLookupForm";
import { loadQrPublicBaseUrl } from "./qrLabelUtils";
import {
    parseAnikeScanUrl,
    assetRouteFor,
    scanFailureCode,
    SCAN_FAILURE_MESSAGES,
    toSelectedAsset
} from "./qrScanUtils";
import { scanAsset } from "../app/UrlCrud";
import "./QrScanner.scss";

const NOT_ANIKE_QR = "This QR code is not an Anike asset QR code.";
const SCAN_INTERVAL_MS = 150;
const MAX_FRAME_WIDTH = 640;

const CAMERA_MESSAGES = {
    denied:
        "Camera access is required to scan an asset QR code. Please allow camera access in your browser settings.",
    unavailable: "Camera scanning isn't available on this device.",
    insecure:
        "Camera scanning needs a secure (HTTPS) connection. Please open Anike using its https:// address.",
    error: "The camera couldn't be started. Please try again."
};

const classifyCameraError = error => {
    const name = error && error.name;
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") return "denied";
    if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") return "unavailable";
    return "error";
};

// Everything here runs in the browser: frames are drawn to a canvas,
// decoded locally, and discarded. Nothing is stored or uploaded - the only
// output is the decoded string. Without a workflow callback that becomes a
// navigation to the existing /scan/:qrToken route; with one, the token is
// resolved through that same authorised endpoint and only the resulting
// asset context (never the token) is handed back. The backend always does
// the authorisation.
function ScannerBody({ onClose, request }) {
    const history = useHistory();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const handledRef = useRef(false);
    const closedRef = useRef(false);
    const resolvingRef = useRef(false);
    const lastFailedRef = useRef(null);
    const baseUrlRef = useRef(null);
    const onAsset = request && request.onAsset;

    const [status, setStatus] = useState("starting");
    const [notice, setNotice] = useState(null);
    const [resolving, setResolving] = useState(false);
    const [showLookup, setShowLookup] = useState(false);
    const [attempt, setAttempt] = useState(0);

    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
    }, []);

    const finish = route => {
        handledRef.current = true;
        stopStream();
        onClose();
        history.push(route);
    };
    // The scan loop must not restart the camera when the parent re-renders.
    const finishRef = useRef(finish);
    finishRef.current = finish;

    const deliverAsset = data => {
        handledRef.current = true;
        stopStream();
        onClose();
        onAsset(toSelectedAsset(data));
    };

    // Workflow mode: authorise the token server-side, then hand back the
    // asset context. A failure keeps the camera running so the user can try
    // another label; the same bad token is not re-submitted every frame.
    const resolveToken = token => {
        if (resolvingRef.current || token === lastFailedRef.current) return;
        resolvingRef.current = true;
        setResolving(true);
        setNotice(null);
        (request.resolveToken || scanAsset)(token)
            .then(({ data }) => {
                resolvingRef.current = false;
                if (closedRef.current) return;
                deliverAsset(data);
            })
            .catch(error => {
                resolvingRef.current = false;
                lastFailedRef.current = token;
                if (closedRef.current) return;
                setResolving(false);
                setNotice(SCAN_FAILURE_MESSAGES[scanFailureCode(error)].text);
            });
    };
    const resolveRef = useRef(resolveToken);
    resolveRef.current = resolveToken;
    const workflowRef = useRef(!!onAsset);
    workflowRef.current = !!onAsset;

    useEffect(() => {
        let cancelled = false;
        let timer = null;
        handledRef.current = false;
        setNotice(null);

        const handleDecoded = text => {
            if (handledRef.current) return;
            const token = parseAnikeScanUrl(text, baseUrlRef.current);
            if (!token) {
                setNotice(NOT_ANIKE_QR);
                return;
            }
            if (workflowRef.current) {
                resolveRef.current(token);
                return;
            }
            finishRef.current(`/scan/${encodeURIComponent(token)}`);
        };

        const start = async () => {
            const media = navigator.mediaDevices;
            if (!media || !media.getUserMedia) {
                setStatus(window.isSecureContext === false ? "insecure" : "unavailable");
                return;
            }
            setStatus("starting");
            try {
                const [jsQRModule, baseUrl] = await Promise.all([import("jsqr"), (request && request.loadBaseUrl ? request.loadBaseUrl() : loadQrPublicBaseUrl())]);
                const jsQR = jsQRModule.default || jsQRModule;
                baseUrlRef.current = baseUrl;

                const stream = await media.getUserMedia({
                    video: { facingMode: { ideal: "environment" } },
                    audio: false
                });
                if (cancelled) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                streamRef.current = stream;
                const video = videoRef.current;
                video.srcObject = stream;
                if (video.play) await Promise.resolve(video.play()).catch(() => {});
                if (cancelled) return;
                setStatus("scanning");

                const tick = () => {
                    if (cancelled || handledRef.current) return;
                    const v = videoRef.current;
                    const canvas = canvasRef.current;
                    if (v && canvas && v.readyState >= 2 && v.videoWidth > 0) {
                        const scale = Math.min(1, MAX_FRAME_WIDTH / v.videoWidth);
                        const w = Math.round(v.videoWidth * scale);
                        const h = Math.round(v.videoHeight * scale);
                        canvas.width = w;
                        canvas.height = h;
                        const ctx = canvas.getContext("2d");
                        if (ctx) {
                            ctx.drawImage(v, 0, 0, w, h);
                            const frame = ctx.getImageData(0, 0, w, h);
                            const code = jsQR(frame.data, w, h, { inversionAttempts: "dontInvert" });
                            if (code && code.data) handleDecoded(code.data);
                        }
                    }
                    if (!cancelled && !handledRef.current) timer = setTimeout(tick, SCAN_INTERVAL_MS);
                };
                tick();
            } catch (error) {
                if (!cancelled) setStatus(classifyCameraError(error));
            }
        };

        start();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            stopStream();
        };
    }, [attempt, stopStream]);

    const closeNow = () => {
        closedRef.current = true;
        stopStream();
        onClose();
    };

    useEffect(
        () => () => {
            closedRef.current = true;
        },
        []
    );

    const failed = !!CAMERA_MESSAGES[status];
    const lookupVisible = showLookup || failed;

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title id="qr-scanner-title">{(request && request.title) || "Scan QR"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!failed && (
                    <div className="qr-scanner">
                        <video
                            ref={videoRef}
                            className="qr-scanner__video"
                            playsInline
                            muted
                            autoPlay
                            aria-label="Camera preview"
                        />
                        <canvas ref={canvasRef} className="qr-scanner__canvas" aria-hidden="true" />
                        {status === "scanning" && <div className="qr-scanner__guide" aria-hidden="true" />}
                    </div>
                )}

                <p className="qr-scanner__status" role="status" aria-live="polite">
                    {status === "starting" && "Starting camera..."}
                    {status === "scanning" && resolving && "Checking the QR code..."}
                    {status === "scanning" && !resolving && !notice && "Point the camera at the Anike QR label."}
                </p>

                {notice && (
                    <div className="alert alert-warning" role="alert">
                        {notice}
                    </div>
                )}

                {failed && (
                    <div className="alert alert-danger" role="alert">
                        {CAMERA_MESSAGES[status]}
                    </div>
                )}

                {failed && status !== "unavailable" && status !== "insecure" && (
                    <button type="button" className="btn btn-light-primary mb-4" onClick={() => setAttempt(a => a + 1)}>
                        Try camera again
                    </button>
                )}

                {!lookupVisible && (
                    <p className="mb-0">
                        Can't scan?{" "}
                        <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setShowLookup(true)}>
                            Enter Asset ID
                        </button>
                    </p>
                )}

                {lookupVisible && (
                    <div className="mt-2">
                        <AssetIdLookupForm
                            lookup={request && request.lookupAssetId}
                            idPrefix="scanner"
                            label="Can't scan? Enter the Asset ID"
                            onResolved={data => (onAsset ? deliverAsset(data) : finish(assetRouteFor(data)))}
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={closeNow}>
                    Use phone camera instead
                </button>
                <button type="button" className="btn btn-primary" onClick={closeNow}>
                    Close
                </button>
            </Modal.Footer>
        </>
    );
}

export default function QrScannerModal({ show, onHide, request }) {
    return (
        <Modal show={show} onHide={onHide} centered backdrop="static" aria-labelledby="qr-scanner-title">
            {show && <ScannerBody onClose={onHide} request={request} />}
        </Modal>
    );
}
